import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useDialog } from "@/context/DialogContext";
import { BACKEND_URL } from "@/lib/config";
import {
  ArrowLeft, Clock, ImageSquare, Trash, ArrowsClockwise, X,
} from "@phosphor-icons/react";

const fmtSecs = (s) => {
  s = Math.max(0, Math.round(s || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${sec}s`;
  return `${sec}s`;
};

const fmtTime = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

export default function IdleTrackerPage() {
  const { user } = useAuth();
  const { confirm, alert } = useDialog();
  const [users, setUsers] = useState([]);
  const [selectedUid, setSelectedUid] = useState("");
  const [report, setReport] = useState(null);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [viewingScreenshot, setViewingScreenshot] = useState(null);

  // ---- Bulk-selection state ----
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [showRangeModal, setShowRangeModal] = useState(false);
  const [rangeFrom, setRangeFrom] = useState(new Date().toISOString().slice(0, 10));
  const [rangeTo, setRangeTo] = useState(new Date().toISOString().slice(0, 10));
  const [rangeScope, setRangeScope] = useState("user"); // "user" | "all"

  const clearSelection = () => setSelectedIds(new Set());
  const toggleOne = (sid) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(sid)) next.delete(sid); else next.add(sid);
      return next;
    });
  };
  const toggleAll = (allIds) => {
    setSelectedIds(prev => {
      if (allIds.every(id => prev.has(id)) && prev.size === allIds.length) return new Set();
      return new Set(allIds);
    });
  };

  const loadUsers = async () => {
    setErr("");
    try {
      const r = await api.get("/idle-tracker/users");
      setUsers(r.data.users || []);
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const loadReport = async (uid, d) => {
    if (!uid) { setReport(null); return; }
    setLoading(true); setErr("");
    try {
      const r = await api.get(`/idle-tracker/user/${uid}`, { params: { date: d } });
      setReport(r.data);
      clearSelection();
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadUsers(); }, []);
  useEffect(() => {
    if (selectedUid) loadReport(selectedUid, date);
  }, [selectedUid, date]);

  const deleteScreenshot = async (sid) => {
    const ok = await confirm({
      title: "Delete this screenshot?",
      description: "The screenshot will be permanently removed.",
      confirmText: "Delete",
      variant: "destructive",
    });
    if (!ok) return;
    try {
      await api.delete(`/idle-tracker/screenshots/${sid}`);
      loadReport(selectedUid, date);
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const bulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    const ok = await confirm({
      title: `Delete ${ids.length} screenshot${ids.length === 1 ? "" : "s"}?`,
      description: "The selected screenshots will be permanently deleted.",
      confirmText: "Delete",
      variant: "destructive",
    });
    if (!ok) return;
    setBulkBusy(true); setErr("");
    try {
      const r = await api.post("/idle-tracker/screenshots/bulk-delete", { ids });
      const deleted = r.data?.deleted ?? ids.length;
      clearSelection();
      await loadReport(selectedUid, date);
      await alert({ title: "Deleted", description: `${deleted} screenshot${deleted === 1 ? "" : "s"} removed.`, confirmText: "OK" });
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBulkBusy(false); }
  };

  const deleteRange = async () => {
    if (!rangeFrom || !rangeTo) { setErr("Please pick both dates"); return; }
    if (rangeFrom > rangeTo) { setErr("From date must be before To date"); return; }
    const target = rangeScope === "user"
      ? `for ${report?.user?.name || "selected user"}`
      : "for ALL users";
    const ok = await confirm({
      title: "Delete screenshots in date range?",
      description: `Every screenshot from ${rangeFrom} through ${rangeTo} ${target} will be permanently deleted. This cannot be undone.`,
      confirmText: "Delete range",
      variant: "destructive",
    });
    if (!ok) return;
    setBulkBusy(true); setErr("");
    try {
      const payload = { date_from: rangeFrom, date_to: rangeTo };
      if (rangeScope === "user" && selectedUid) payload.user_id = selectedUid;
      const r = await api.post("/idle-tracker/screenshots/delete-range", payload);
      const deleted = r.data?.deleted ?? 0;
      setShowRangeModal(false);
      await loadReport(selectedUid, date);
      await alert({ title: "Deleted", description: `${deleted} screenshot${deleted === 1 ? "" : "s"} removed.`, confirmText: "OK" });
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBulkBusy(false); }
  };

  const idleWindows = report?.events?.filter(e => e.type === "idle_end") || [];
  const heartbeats = report?.events?.filter(e => e.type === "heartbeat") || [];
  const allScreenshotIds = (report?.screenshots || []).map(s => s.id);
  const allSelected = allScreenshotIds.length > 0 && allScreenshotIds.every(id => selectedIds.has(id));
  const anySelected = selectedIds.size > 0;
  const canAdminDelete = user?.role === "admin" || user?.role === "manager";

  return (
    <div>
      <div className="border-b border-border px-8 lg:px-12 py-6">
        <div className="eyebrow mb-2">Monitoring · Idle screen tracker</div>
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight" data-testid="idle-tracker-title">
              Idle time & screenshots
            </h1>
            <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
              Track how long users are idle during their working hours. If screenshot capture is enabled,
              browse the periodic snapshots to verify what they were working on.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={loadUsers} className="border border-border px-3 py-2 text-sm hover:bg-muted flex items-center gap-2">
              <ArrowsClockwise size={14} weight="bold" /> Refresh
            </button>
            <Link to="/settings?tab=idle_tracker" className="border border-border px-3 py-2 text-sm hover:bg-muted">Settings</Link>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6 p-6 lg:p-8">
        {/* User list */}
        <aside className="border border-border">
          <div className="px-4 py-3 border-b border-border eyebrow bg-muted">
            Tracked users ({users.length})
          </div>
          {users.length === 0 ? (
            <div className="p-4 text-sm text-muted-foreground">
              No users are currently being tracked. Grant the <b>use_idle_tracker</b> capability to a role to include those users.
            </div>
          ) : (
            <div className="max-h-[70vh] overflow-y-auto">
              {users.map(u => (
                <button
                  key={u.id}
                  onClick={() => setSelectedUid(u.id)}
                  className={`w-full text-left px-4 py-3 border-b border-border hover:bg-muted/50 flex items-center gap-3 ${selectedUid === u.id ? "bg-blue-50" : ""}`}
                  data-testid={`user-${u.id}`}
                >
                  <div className="w-8 h-8 shrink-0 bg-[#0A0A0A] text-white flex items-center justify-center font-bold text-xs">
                    {u.name?.[0]?.toUpperCase() || "?"}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{u.name}</div>
                    <div className="text-[11px] text-muted-foreground truncate font-mono-mini">
                      {u.department || u.role}
                    </div>
                    <div className="text-[10px] font-mono-mini mt-0.5">
                      <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1 align-middle ${u.is_checked_in ? "bg-green-500" : "bg-slate-300"}`} />
                      {u.is_checked_in ? "Checked in" : "Off duty"}
                      {" · "}
                      {fmtSecs(u.idle_seconds_today)} idle
                      {" · "}
                      {u.screenshot_count_today} snap
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </aside>

        {/* Report */}
        <section className="min-w-0">
          {err && <div className="border border-[#FF3B30] bg-[#FFF5F5] text-[#FF3B30] px-4 py-2 text-sm mb-4">{err}</div>}
          {!selectedUid ? (
            <div className="border border-border p-12 text-center text-sm text-muted-foreground">
              Select a user on the left to see their timeline and screenshots.
            </div>
          ) : loading ? (
            <div className="p-8 text-sm text-muted-foreground">Loading…</div>
          ) : !report ? null : (
            <div className="space-y-6">
              {/* Header */}
              <div className="border border-border p-5 flex items-center justify-between flex-wrap gap-3">
                <div>
                  <div className="font-display text-xl font-bold">{report.user.name}</div>
                  <div className="text-xs text-muted-foreground">{report.user.email} · {report.user.department}</div>
                </div>
                <div className="flex items-center gap-2">
                  <label className="eyebrow text-[10px]">Date</label>
                  <input
                    type="date"
                    value={date}
                    onChange={e => setDate(e.target.value)}
                    className="border border-border px-3 py-1.5 text-sm"
                    data-testid="idle-date-picker"
                  />
                </div>
              </div>

              {/* Summary tiles */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <SummaryTile label="Total idle" value={fmtSecs(report.summary.total_idle_seconds)} />
                <SummaryTile label="Idle sessions" value={report.summary.idle_session_count} />
                <SummaryTile label="Screenshots" value={report.summary.screenshot_count} />
                <SummaryTile label="Events" value={report.summary.event_count} />
              </div>

              {/* Idle timeline */}
              <div className="border border-border">
                <div className="px-5 py-3 border-b border-border eyebrow flex items-center gap-2">
                  <Clock size={12} weight="bold" /> Idle timeline
                </div>
                {idleWindows.length === 0 ? (
                  <div className="p-5 text-sm text-muted-foreground">No idle periods recorded on this day.</div>
                ) : (
                  <div className="divide-y divide-border">
                    {idleWindows.map(e => (
                      <div key={e.id} className="px-5 py-3 grid grid-cols-[100px_1fr_auto] gap-3 items-center text-sm">
                        <div className="font-mono-mini text-xs">{fmtTime(e.started_at)} → {fmtTime(e.ended_at)}</div>
                        <div className="text-xs text-muted-foreground truncate">{e.page || "—"}</div>
                        <div className="font-mono-mini text-xs font-bold text-orange-600">{fmtSecs(e.duration_seconds)}</div>
                      </div>
                    ))}
                  </div>
                )}
                <div className="px-5 py-2 border-t border-border text-[10px] font-mono-mini text-muted-foreground">
                  {heartbeats.length} heartbeats received
                </div>
              </div>

              {/* Screenshots */}
              <div className="border border-border">
                <div className="px-5 py-3 border-b border-border eyebrow flex items-center gap-2 flex-wrap">
                  <ImageSquare size={12} weight="bold" /> Screenshots ({report.screenshots.length})
                  {report.screenshots.length > 0 && (
                    <label className="ml-2 inline-flex items-center gap-2 normal-case text-[11px] font-mono-mini cursor-pointer" data-testid="screenshots-select-all-wrap">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={() => toggleAll(allScreenshotIds)}
                        className="cursor-pointer"
                        data-testid="screenshots-select-all"
                      />
                      Select all
                    </label>
                  )}
                  {anySelected && (
                    <>
                      <span className="text-[11px] font-mono-mini text-muted-foreground">
                        · {selectedIds.size} selected
                      </span>
                      <button
                        onClick={bulkDelete}
                        disabled={bulkBusy}
                        className="ml-1 border border-[#FF3B30] bg-white text-[#FF3B30] px-2 py-1 text-[11px] font-mono-mini hover:bg-[#FFF5F5] disabled:opacity-50 flex items-center gap-1"
                        data-testid="screenshots-bulk-delete"
                      >
                        <Trash size={11} weight="bold" /> Delete selected
                      </button>
                      <button
                        onClick={clearSelection}
                        className="text-[11px] font-mono-mini text-muted-foreground hover:underline"
                        data-testid="screenshots-clear-selection"
                      >
                        Clear
                      </button>
                    </>
                  )}
                  {canAdminDelete && (
                    <button
                      onClick={() => setShowRangeModal(true)}
                      className="ml-auto border border-border px-2 py-1 text-[11px] font-mono-mini hover:bg-muted flex items-center gap-1"
                      data-testid="screenshots-delete-range-btn"
                    >
                      <Trash size={11} weight="bold" /> Delete in date range…
                    </button>
                  )}
                </div>
                {report.screenshots.length === 0 ? (
                  <div className="p-5 text-sm text-muted-foreground">No screenshots on this day.</div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 p-4">
                    {report.screenshots.map(s => {
                      const isSel = selectedIds.has(s.id);
                      return (
                        <div key={s.id} className={`border ${isSel ? "border-[#002FA7] ring-2 ring-[#002FA7]/30" : "border-border"} group relative`}>
                          <label
                            className="absolute top-1.5 left-1.5 z-10 bg-white border border-border px-1.5 py-0.5 flex items-center gap-1 text-[10px] font-mono-mini shadow-sm cursor-pointer"
                            onClick={(e) => e.stopPropagation()}
                            data-testid={`screenshot-select-wrap-${s.id}`}
                          >
                            <input
                              type="checkbox"
                              checked={isSel}
                              onChange={() => toggleOne(s.id)}
                              className="cursor-pointer"
                              data-testid={`screenshot-select-${s.id}`}
                            />
                          </label>
                          <img
                            src={`${BACKEND_URL}/api/idle-tracker/screenshot/${s.id}`}
                            alt={fmtTime(s.taken_at)}
                            className="w-full h-32 object-cover cursor-pointer bg-slate-100"
                            onClick={() => setViewingScreenshot(s)}
                            data-testid={`screenshot-${s.id}`}
                          />
                          <div className="px-2 py-1 flex items-center justify-between text-[10px] font-mono-mini bg-white">
                            <span>{fmtTime(s.taken_at)}</span>
                            <button onClick={() => deleteScreenshot(s.id)} className="text-red-600 hover:underline" data-testid={`screenshot-delete-${s.id}`}>
                              <Trash size={12} weight="bold" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </section>
      </div>

      {/* Screenshot preview modal */}
      {viewingScreenshot && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-6" onClick={() => setViewingScreenshot(null)}>
          <div className="max-w-6xl w-full max-h-full flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between text-white text-sm mb-2">
              <div className="font-mono-mini">
                {fmtTime(viewingScreenshot.taken_at)} · {viewingScreenshot.page || "—"}
              </div>
              <button onClick={() => setViewingScreenshot(null)} className="p-2 hover:bg-white/20">
                <X size={20} weight="bold" />
              </button>
            </div>
            <img
              src={`${BACKEND_URL}/api/idle-tracker/screenshot/${viewingScreenshot.id}`}
              alt=""
              className="max-w-full max-h-[85vh] object-contain bg-white"
            />
          </div>
        </div>
      )}
      {/* Delete-by-date-range modal */}
      {showRangeModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => !bulkBusy && setShowRangeModal(false)}>
          <div className="bg-white border border-border shadow-xl w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="delete-range-modal">
            <div className="px-5 py-3 border-b border-border flex items-center justify-between">
              <div className="font-display text-lg font-bold">Delete screenshots in range</div>
              <button onClick={() => setShowRangeModal(false)} className="p-1 hover:bg-muted" data-testid="delete-range-close">
                <X size={16} />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-xs text-muted-foreground">
                Permanently deletes every screenshot captured within this date range. This cannot be undone.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="eyebrow block mb-1 text-[10px]">From</label>
                  <input
                    type="date"
                    value={rangeFrom}
                    onChange={e => setRangeFrom(e.target.value)}
                    className="w-full border border-border px-3 py-2 text-sm"
                    data-testid="delete-range-from"
                  />
                </div>
                <div>
                  <label className="eyebrow block mb-1 text-[10px]">To</label>
                  <input
                    type="date"
                    value={rangeTo}
                    onChange={e => setRangeTo(e.target.value)}
                    className="w-full border border-border px-3 py-2 text-sm"
                    data-testid="delete-range-to"
                  />
                </div>
              </div>
              <div>
                <label className="eyebrow block mb-1 text-[10px]">Scope</label>
                <div className="flex gap-2 text-sm">
                  <label className={`flex-1 border px-3 py-2 cursor-pointer ${rangeScope === "user" ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-border"}`}>
                    <input
                      type="radio"
                      name="rangeScope"
                      checked={rangeScope === "user"}
                      onChange={() => setRangeScope("user")}
                      className="mr-2"
                      data-testid="delete-range-scope-user"
                    />
                    Selected user only
                  </label>
                  <label className={`flex-1 border px-3 py-2 cursor-pointer ${rangeScope === "all" ? "border-[#FF3B30] bg-[#FF3B30] text-white" : "border-border"}`}>
                    <input
                      type="radio"
                      name="rangeScope"
                      checked={rangeScope === "all"}
                      onChange={() => setRangeScope("all")}
                      className="mr-2"
                      data-testid="delete-range-scope-all"
                    />
                    All users
                  </label>
                </div>
              </div>
              {err && <div className="border border-[#FF3B30] bg-[#FFF5F5] text-[#FF3B30] px-3 py-2 text-xs">{err}</div>}
            </div>
            <div className="px-5 py-3 border-t border-border flex justify-end gap-2">
              <button onClick={() => setShowRangeModal(false)} disabled={bulkBusy} className="border border-border px-3 py-2 text-sm hover:bg-muted disabled:opacity-50">
                Cancel
              </button>
              <button
                onClick={deleteRange}
                disabled={bulkBusy}
                className="bg-[#FF3B30] text-white px-3 py-2 text-sm font-bold hover:bg-[#e12e24] disabled:opacity-50 flex items-center gap-2"
                data-testid="delete-range-confirm"
              >
                <Trash size={12} weight="bold" /> {bulkBusy ? "Deleting…" : "Delete range"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryTile({ label, value }) {
  return (
    <div className="border border-border p-4">
      <div className="eyebrow text-[10px]">{label}</div>
      <div className="font-display text-2xl font-black mt-1">{value}</div>
    </div>
  );
}
